/**
 * ActivityPage — three tabs that make the three entities of the
 * redesign (Suggestion / Vote / ExecutedAction) explicit:
 *
 *   On YouTube (default)  — outcomes that actually ran on the kid's feed
 *   My decisions          — votes the parent cast in the app
 *   Pending               — slim callout that deep-links to /review/pending
 *
 * Tabs are URL-routed so deep-links land on the right tab. The page
 * mounts at /profile/:pid/activity/* and reads the trailing segment
 * to pick the active tab.
 *
 * Layout on the YouTube / Decisions tabs is a two-column grid: the
 * row list on the left and a docked EvidenceSheet on the right that
 * opens when a row is clicked. The Pending tab body is a single
 * callout card (the real list lives at /review/pending and we never
 * duplicate it here — that was the IA mistake §2.1 in the redesign
 * brief warned against).
 *
 * Translated from design/pages/mobile/ActivityPage.jsx — same
 * vocabulary, ordering, light palette; rendered with MUI 7.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams, useNavigate, useLocation, Link} from 'react-router-dom';
import {getAuth} from 'firebase/auth';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import SkipNextOutlined from '@mui/icons-material/SkipNextOutlined';
import {
  Evidence,
  ExecutedAction,
  Suggestion,
  SuggestionVerb,
  Vote,
  Author,
  Video,
} from '../../../types/generated/graphql';
import {useExecutedActions, useMyVotes, useWeeklyImpact} from '../../../api/graphql_activity';
import {useGetPendingSuggestions} from '../../../api/graphql_suggestions';
import {useRetractVerdict} from '../../../api/graphql_verdict_actions';
import {track} from '../../../lib/telemetry';
import {VerbChip} from '../VerbChip';
import {
  EvidenceSheet,
  EvidenceSheetData,
  EvidenceSheetMode,
  EvidenceSheetPersona,
  VERB_TONES,
  verbTokenForVerb,
} from '../EvidenceSheet';
import {DayBucket, formatWhen, groupByDay} from './dayGroups';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

type TabKey = 'youtube' | 'decisions' | 'pending';

function tabFromPath(pathname: string): TabKey {
  if (pathname.endsWith('/decisions')) return 'decisions';
  if (pathname.endsWith('/pending')) return 'pending';
  return 'youtube';
}

function targetName(target: Suggestion['target'] | Vote['target'] | ExecutedAction['target']): string {
  if (target && 'title' in (target as Record<string, unknown>)) {
    return (target as Video).title ?? 'Untitled video';
  }
  return (target as Author)?.name ?? 'Channel';
}

function targetKind(target: Suggestion['target'] | Vote['target'] | ExecutedAction['target']): 'video' | 'channel' {
  return target && 'title' in (target as Record<string, unknown>) ? 'video' : 'channel';
}

function thumbnailUrlFor(target: Suggestion['target'] | Vote['target'] | ExecutedAction['target']): string | null {
  if (!target) return null;
  if ('title' in (target as Record<string, unknown>)) {
    const v = target as Video;
    return v.thumbnails?.[0]?.url ?? v.author?.thumbnails?.[0]?.url ?? null;
  }
  const a = target as Author;
  return a.thumbnails?.[0]?.url ?? null;
}

function useIsAdmin(): boolean {
  // Mirrors the persona check in PendingSuggestionsPage — read the
  // role claim off the firebase id token. No redux slice for this in
  // the repo today.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const user = getAuth().currentUser;
    if (!user) return;
    let cancelled = false;
    user
      .getIdTokenResult()
      .then(t => {
        if (!cancelled) setIsAdmin(t.claims.role === 'admin');
      })
      .catch(err => console.warn('[ActivityPage] admin claim check failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);
  return isAdmin;
}

// ─── Tabs bar ──────────────────────────────────────────────────────────────

interface TabDef {
  id: TabKey;
  label: string;
  sub: string;
  count: number;
}

function TabsBar({tabs, active, onChange}: {
  tabs: TabDef[];
  active: TabKey;
  onChange: (id: TabKey) => void;
}): React.JSX.Element {
  // Three bold dark pills. Each pill has the label + a tight inline
  // secondary count number to its right (smaller, dimmed). Active =
  // bg-slate-900 + white text; inactive = slate-100 + slate-900.
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#F8FAFC',
        py: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          '& > *': {flex: '1 1 0', minWidth: 0},
        }}
      >
        {tabs.map(t => {
          const isActive = t.id === active;
          return (
            <Box
              component="button"
              key={t.id}
              onClick={() => onChange(t.id)}
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: '6px',
                borderRadius: '999px',
                border: 'none',
                cursor: 'pointer',
                px: 1.5,
                py: 1.25,
                backgroundColor: isActive ? '#0F172A' : '#F1F5F9',
                color: isActive ? '#FFFFFF' : '#0F172A',
                '&:hover': {
                  backgroundColor: isActive ? '#0F172A' : '#E2E8F0',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  fontVariantNumeric: 'tabular-nums',
                  color: isActive ? 'rgba(255,255,255,0.7)' : '#94A3B8',
                  flexShrink: 0,
                }}
              >
                {t.count}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function TabSubhead({text}: {text: string}): React.JSX.Element {
  // Plain inline subhead — no full-bleed strip. The outer page
  // Container handles centering; the subhead just flows with the
  // rest of the body content.
  return (
    <Box sx={{py: 1, mb: 1}}>
      <Typography sx={{color: '#64748B', fontSize: '13px', lineHeight: 1.4}}>
        {text}
      </Typography>
    </Box>
  );
}

function DayHeader({label, count}: {label: string; count: number}): React.JSX.Element {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      sx={{px: 3, pt: 2, pb: 0.5}}
    >
      <Typography
        component="h2"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          color: '#64748B',
          fontSize: '10px',
          letterSpacing: '0.1em',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{color: '#94A3B8', fontSize: '10px'}}>
        {count} {count === 1 ? 'item' : 'items'}
      </Typography>
    </Stack>
  );
}

function GroupedList<T extends {id: string}>({
  groups,
  renderItem,
}: {
  groups: DayBucket<T>[];
  renderItem: (item: T) => React.ReactNode;
}): React.JSX.Element {
  return (
    <>
      {groups.map(g => (
        <Box key={g.key}>
          <DayHeader label={g.label} count={g.items.length} />
          <Box sx={{borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF'}}>
            {g.items.map((it, i) => (
              <Box
                key={it.id}
                sx={{borderTop: i === 0 ? 'none' : '1px solid #F1F5F9'}}
              >
                {renderItem(it)}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </>
  );
}

// ─── Row thumbnail ─────────────────────────────────────────────────────────

function RowThumbnail({
  target,
  verb,
}: {
  target: Vote['target'] | ExecutedAction['target'] | Suggestion['target'];
  verb: SuggestionVerb;
}): React.JSX.Element {
  // 44×44 image-or-fallback at the start of every Activity row. The
  // fallback colour ties to the verb so a row reads as block-ish /
  // follow-ish even before the parent reads the title.
  const url = thumbnailUrlFor(target);
  const kind = targetKind(target);
  const radius = kind === 'channel' ? '50%' : '8px';
  if (url) {
    return (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          width: 44,
          height: 44,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          backgroundColor: '#F1F5F9',
        }}
      />
    );
  }
  const t = VERB_TONES[verbTokenForVerb(verb)];
  const Icon =
    verb === SuggestionVerb.Block
      ? BlockOutlined
      : verb === SuggestionVerb.Follow
        ? CheckCircleOutline
        : verb === SuggestionVerb.Boost
          ? TrendingUpOutlined
          : SkipNextOutlined;
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: radius,
        backgroundColor: t.pillBgLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{fontSize: 18, color: t.pillTextLight}} />
    </Box>
  );
}

// ─── On YouTube tab ────────────────────────────────────────────────────────

function YouTubeRow({
  item,
  selected,
  onSelect,
}: {
  item: ExecutedAction;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <Box
      component="button"
      onClick={onSelect}
      aria-expanded={selected}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        px: 3,
        py: 1.5,
        boxShadow: selected ? 'inset 2px 0 0 #6366F1' : 'none',
        backgroundColor: selected ? '#EEF2FF' : 'transparent',
        '&:hover': {backgroundColor: selected ? '#EEF2FF' : '#F8FAFC'},
      }}
    >
      <RowThumbnail target={item.target} verb={item.verb} />
      <Stack alignItems="center" sx={{pt: '2px'}}>
        {/* Past tense — executed actions on the kid's feed. */}
        <VerbChip verb={item.verb} tense="past" palette="light" />
      </Stack>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#0F172A',
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {targetName(item.target)}
          </Typography>
          <Typography sx={{color: '#94A3B8', fontSize: '11px', flexShrink: 0}}>
            {formatWhen(item.occurredAt)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{mt: 0.25}}>
          <Box component="span" sx={{color: '#94A3B8', fontSize: '12px'}}>⟶</Box>
          <Typography sx={{color: '#334155', fontSize: '12px', lineHeight: 1.45}}>
            {item.outcome}
          </Typography>
        </Stack>
        {item.detail && (
          <Typography sx={{mt: 0.25, color: '#94A3B8', fontSize: '11px', lineHeight: 1.4}}>
            {item.detail}
          </Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{fontSize: 16, color: '#CBD5E1', mt: 0.5, flexShrink: 0}} />
    </Box>
  );
}

function YouTubeBanner({impact}: {impact: {videosRemoved: number; videosSurfaced: number; candidatesQueued: number} | null}): React.JSX.Element {
  // Per the wiring rules: when no executions have produced metrics
  // yet, render an em dash rather than fake zeros.
  const empty = !impact || (impact.videosRemoved === 0 && impact.videosSurfaced === 0 && impact.candidatesQueued === 0);
  return (
    <Box
      sx={{
        mx: 3,
        mt: 1.5,
        borderRadius: '12px',
        border: '1px solid #A7F3D0',
        backgroundColor: '#ECFDF5',
        // Asymmetric: less below the body text so the trailing line-
        // height doesn't stack with the container padding.
        px: 1.5,
        pt: 1.5,
        pb: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <CheckCircleOutline sx={{fontSize: 14, color: '#059669'}} />
        <Typography
          sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#047857',
            fontSize: '10px',
            letterSpacing: '0.08em',
          }}
        >
          This week&apos;s impact
        </Typography>
      </Stack>
      <Typography sx={{mt: 0.5, color: '#0F172A', fontSize: '12.5px', lineHeight: 1.45}}>
        {empty
          ? // Two short lines in the empty state per the §7 refinement —
            // one line when populated, two when there's nothing yet.
            "Nothing's hit YouTube yet this week."
          : (
            <>
              <Box component="span" sx={{fontWeight: 600}}>{impact!.videosRemoved} videos removed</Box>,{' '}
              <Box component="span" sx={{fontWeight: 600}}>{impact!.videosSurfaced} new uploads surfaced</Box>,{' '}
              <Box component="span" sx={{fontWeight: 600}}>{impact!.candidatesQueued} queued</Box>.
            </>
          )}
      </Typography>
      {empty && (
        <Typography sx={{mt: 0.25, color: '#475569', fontSize: '12px', lineHeight: 1.45}}>
          Outcomes show up here within a day of applying a suggestion.
        </Typography>
      )}
    </Box>
  );
}

function buildVoteEvidence(rationale: string | null | undefined): Evidence {
  // Vote rows don't carry a backing Suggestion's evidence today (the
  // resolver returns suggestionId=null). Synthesize a minimal stub so
  // the EvidenceSheet renders with the rationale as the verdict and an
  // em-dash for the missing video grid. TODO(activity-evidence): when
  // resolvers hydrate evidence from the matched corrective action,
  // pass it through instead of stubbing.
  return {
    oneLine: rationale ?? '',
    videos: [],
    preview: {headline: '', bullets: [], reversibleNote: ''},
    scoring: null,
  };
}

function buildExecutedEvidence(outcome: string): Evidence {
  return {
    oneLine: outcome,
    videos: [],
    preview: {headline: '', bullets: [], reversibleNote: ''},
    scoring: null,
  };
}

function targetThumb(t: Vote['target'] | ExecutedAction['target']): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const x = t as any;
  return x?.thumbnails?.[0]?.url ?? x?.author?.thumbnails?.[0]?.url ?? null;
}

function sheetDataForVote(v: Vote): EvidenceSheetData {
  return {
    verb: v.verb,
    target: {
      kind: targetKind(v.target),
      name: targetName(v.target),
      thumbnailUrl: targetThumb(v.target),
    },
    evidence: buildVoteEvidence(v.rationale),
    contextLine: v.rationale ?? null,
  };
}

function sheetDataForExecuted(a: ExecutedAction): EvidenceSheetData {
  return {
    verb: a.verb,
    target: {
      kind: targetKind(a.target),
      name: targetName(a.target),
      thumbnailUrl: targetThumb(a.target),
    },
    evidence: buildExecutedEvidence(a.outcome),
    contextLine: a.detail ? `${a.outcome} · ${a.detail}` : a.outcome,
  };
}

function YouTubeTab({
  client,
  profileId,
  selectedId,
  onSelect,
  persona,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  profileId: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  persona: EvidenceSheetPersona;
}): React.JSX.Element {
  const {data, loading, error} = useExecutedActions(client, profileId);
  const {data: impactData} = useWeeklyImpact(client, profileId);
  const items = useMemo(
    () => (data?.executedActions ?? []).map(a => ({...a, at: a.occurredAt})),
    [data],
  );
  const groups = useMemo(() => groupByDay(items), [items]);
  return (
    <ActivityTwoColumn
      persona={persona}
      selectedId={selectedId}
      onSelect={onSelect}
      sheetMode="readonly"
      sheetDataFor={id => {
        const a = items.find(it => it.id === id);
        return a ? sheetDataForExecuted(a as ExecutedAction) : null;
      }}
      onSheetTelemetry={id => track('activity_row_opened', {tab: 'youtube', id, persona})}
    >
      <YouTubeBanner impact={impactData?.weeklyImpact ?? null} />
      {loading && items.length === 0 && (
        <Stack alignItems="center" sx={{py: 4}}>
          <CircularProgress size={20} sx={{color: '#94A3B8'}} />
        </Stack>
      )}
      {error && (
        <Box sx={{mx: 3, mt: 1.5, color: '#B91C1C', fontSize: '13px'}}>
          Couldn&apos;t load activity: {error.message}
        </Box>
      )}
      {/* Banner already carries the empty-state copy — don't render a
          second EmptyState card below it. Matches the mobile fix. */}
      <GroupedList
        groups={groups}
        renderItem={a => {
          const action = a as ExecutedAction;
          return (
            <YouTubeRow
              item={action}
              selected={selectedId === action.id}
              onSelect={() => onSelect(selectedId === action.id ? null : action.id)}
            />
          );
        }}
      />
    </ActivityTwoColumn>
  );
}

// ─── My decisions tab ──────────────────────────────────────────────────────

function DecisionRow({
  item,
  selected,
  onSelect,
}: {
  item: Vote;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  // Caption = rationale (data-driven, source-aware). Resolver §5
  // returns 5+ distinct strings across a mixed batch; no verb-keyed
  // wrapper here.
  const caption =
    item.rationale && item.rationale.trim().length > 0
      ? item.rationale
      : '—';
  return (
    <Box
      component="button"
      onClick={onSelect}
      aria-expanded={selected}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        px: 3,
        py: 1.5,
        boxShadow: selected ? 'inset 2px 0 0 #6366F1' : 'none',
        backgroundColor: selected ? '#EEF2FF' : 'transparent',
        '&:hover': {backgroundColor: selected ? '#EEF2FF' : '#F8FAFC'},
      }}
    >
      <RowThumbnail target={item.target} verb={item.verb} />
      <Stack alignItems="center" sx={{pt: '2px'}}>
        {/* Imperative — the verb the parent voted FOR. */}
        <VerbChip verb={item.verb} tense="imperative" palette="light" />
      </Stack>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#0F172A',
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {targetName(item.target)}
          </Typography>
          <Typography sx={{color: '#94A3B8', fontSize: '11px', flexShrink: 0}}>
            {formatWhen(item.createdAt)}
          </Typography>
        </Stack>
        <Typography
          sx={{
            mt: 0.25,
            fontWeight: caption === '—' ? 400 : 500,
            color: caption === '—' ? '#94A3B8' : '#475569',
            fontSize: '12px',
            lineHeight: 1.45,
          }}
        >
          {caption}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{fontSize: 16, color: '#CBD5E1', mt: 0.5, flexShrink: 0}} />
    </Box>
  );
}

function DecisionsBanner(): React.JSX.Element {
  return (
    <Box
      sx={{
        mx: 3,
        mt: 1.5,
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
        backgroundColor: '#FFFFFF',
        px: 1.5,
        pt: 1.25,
        pb: 1.25,
      }}
    >
      <Typography sx={{color: '#475569', fontSize: '12.5px', lineHeight: 1.45}}>
        Tap any row to see the evidence and what action GoodShowHQ took
        as a result.
      </Typography>
    </Box>
  );
}

function DecisionsTab({
  client,
  profileId,
  selectedId,
  onSelect,
  persona,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  profileId: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  persona: EvidenceSheetPersona;
}): React.JSX.Element {
  const {data, loading, error, refetch} = useMyVotes(client, profileId);
  const {mutateAsync: retract} = useRetractVerdict(client);
  const items = useMemo(
    () => (data?.myVotes ?? []).map(v => ({...v, at: v.createdAt})),
    [data],
  );
  const groups = useMemo(() => groupByDay(items), [items]);

  const handleUndo = async (voteId: string): Promise<void> => {
    const v = items.find(it => it.id === voteId);
    if (!v) return;
    const videoId =
      v.target && 'title' in (v.target as Record<string, unknown>)
        ? (v.target as Video).id
        : null;
    if (!videoId) return;
    track('decision_undo_clicked', {voteId, verb: v.verb});
    try {
      await retract({profileId, videoId});
      onSelect(null);
      await refetch();
    } catch (err) {
      console.warn('[ActivityPage] retractVerdict failed:', err);
    }
  };

  return (
    <ActivityTwoColumn
      persona={persona}
      selectedId={selectedId}
      onSelect={onSelect}
      sheetMode="history"
      onSheetUndo={selectedId ? () => handleUndo(selectedId) : undefined}
      sheetDataFor={id => {
        const v = items.find(it => it.id === id);
        return v ? sheetDataForVote(v as Vote) : null;
      }}
      onSheetTelemetry={id => track('activity_row_opened', {tab: 'decisions', id, persona})}
    >
      <DecisionsBanner />
      {loading && items.length === 0 && (
        <Stack alignItems="center" sx={{py: 4}}>
          <CircularProgress size={20} sx={{color: '#94A3B8'}} />
        </Stack>
      )}
      {error && (
        <Box sx={{mx: 3, mt: 1.5, color: '#B91C1C', fontSize: '13px'}}>
          Couldn&apos;t load votes: {error.message}
        </Box>
      )}
      {!loading && items.length === 0 && (
        <EmptyState icon={InboxOutlined} title="No decisions logged yet" body="Votes you cast in the app appear here." />
      )}
      <GroupedList
        groups={groups}
        renderItem={v => {
          const vote = v as Vote;
          return (
            <DecisionRow
              item={vote}
              selected={selectedId === vote.id}
              onSelect={() => onSelect(selectedId === vote.id ? null : vote.id)}
            />
          );
        }}
      />
    </ActivityTwoColumn>
  );
}

// ─── Pending tab (slim callout, deep-links to /review/pending) ────────────

function PendingCallout({count, profileId}: {count: number; profileId: string}): React.JSX.Element {
  // Brief §2.1: /activity/pending DOES NOT duplicate the /review/pending
  // list. Just a callout that links out. The mobile app keeps the full
  // list inside Activity because there isn't a separate /review surface
  // on phone — this split is intentional.
  return (
    <Box sx={{mx: 3, mt: 2}}>
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid #FDE68A',
          backgroundColor: '#FFFBEB',
          px: 2.5,
          pt: 2,
          pb: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <WarningAmberOutlined sx={{fontSize: 18, color: '#D97706'}} />
          <Typography
            component="h2"
            sx={{fontWeight: 700, color: '#92400E', fontSize: '15px', lineHeight: 1.25}}
          >
            {count === 0
              ? 'Nothing pending'
              : `${count} suggestion${count === 1 ? '' : 's'} ${count === 1 ? 'is' : 'are'} waiting on you`}
          </Typography>
        </Stack>
        <Typography sx={{color: '#334155', fontSize: '13px', lineHeight: 1.5}}>
          Review is where you act on pending suggestions. Open it to
          see evidence and apply with one click.
        </Typography>
        <Box>
          <Button
            component={Link}
            to={`/profile/${profileId}/review/pending`}
            disabled={count === 0}
            size="medium"
            sx={{
              backgroundColor: '#D97706',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'none',
              borderRadius: '10px',
              px: 2,
              py: 0.75,
              '&:hover': {backgroundColor: '#B45309'},
              '&.Mui-disabled': {backgroundColor: '#FDE68A', color: '#92400E'},
            }}
            endIcon={<ChevronRightIcon sx={{fontSize: 16}} />}
            onClick={() => track('activity_pending_open_review_clicked', {count})}
          >
            Open Review queue
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof InboxOutlined;
  title: string;
  body: string;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        mx: 3,
        mt: 2,
        borderRadius: '12px',
        border: '1px dashed #E2E8F0',
        px: 4,
        pt: 4,
        pb: 3,
        textAlign: 'center',
      }}
    >
      <Icon sx={{fontSize: 32, color: '#CBD5E1', mb: 1}} />
      <Typography sx={{fontWeight: 500, color: '#334155', fontSize: '13px'}}>{title}</Typography>
      <Typography sx={{mt: 0.5, color: '#94A3B8', fontSize: '12px'}}>{body}</Typography>
    </Box>
  );
}

// ─── Layout shell — centered Container + Dialog popup ─────────────────────

function ActivityTwoColumn({
  children,
  persona,
  selectedId,
  onSelect,
  sheetMode,
  sheetDataFor,
  onSheetUndo,
  onSheetTelemetry,
}: {
  children: React.ReactNode;
  persona: EvidenceSheetPersona;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  sheetMode: EvidenceSheetMode;
  sheetDataFor: (id: string) => EvidenceSheetData | null;
  onSheetUndo?: () => void;
  onSheetTelemetry?: (id: string) => void;
}): React.JSX.Element {
  // Fire telemetry once per selection change.
  useEffect(() => {
    if (selectedId && onSheetTelemetry) onSheetTelemetry(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const data = selectedId ? sheetDataFor(selectedId) : null;
  // Layout converged with the rest of /profile/:pid pages: rows in a
  // standard centered Container (maxWidth=md), evidence opens as a
  // Dialog popup rather than a docked right-side aside. Drops the
  // dark slate-950 panel that read as out-of-theme and the
  // full-bleed two-column split that broke the centered visual
  // rhythm every other profile page uses.
  return (
    <>
      <Box sx={{pb: 4}}>{children}</Box>
      <Dialog
        open={!!data}
        onClose={() => onSelect(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{sx: {borderRadius: 2}}}
      >
        {data && (
          <EvidenceSheet
            data={data}
            persona={persona}
            mode={sheetMode}
            onClose={() => onSelect(null)}
            onUndoVote={onSheetUndo}
            forceDocked
          />
        )}
      </Dialog>
    </>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

const SUBHEAD: Record<TabKey, string | null> = {
  youtube: "What happened on your kid's YouTube",
  decisions: 'Your vote history',
  pending: null,
};

export default function ActivityPage({client}: Props): React.JSX.Element {
  const {pid} = useParams<{pid: string}>();
  const profileId = pid as string;
  const navigate = useNavigate();
  const location = useLocation();
  const active = tabFromPath(location.pathname);
  const isAdmin = useIsAdmin();
  const persona: EvidenceSheetPersona = isAdmin ? 'admin' : 'parent';

  // Per-tab selection state. Each tab keeps its own selected row so
  // switching tabs doesn't clear the other tab's docked sheet.
  const [selectedYouTubeId, setSelectedYouTubeId] = useState<string | null>(null);
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);

  // Esc clears any open EvidenceSheet on the active tab.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      if (active === 'youtube') setSelectedYouTubeId(null);
      else if (active === 'decisions') setSelectedDecisionId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  useEffect(() => {
    track('activity_tab_viewed', {tab: active});
  }, [active]);

  // Tab counts are best-effort — only the active tab fetches eagerly;
  // background tabs' counts come from the same queries cached by
  // Apollo if they've been visited.
  const {data: ytData} = useExecutedActions(client, profileId);
  const {data: votesData} = useMyVotes(client, profileId);
  const {data: pendingData} = useGetPendingSuggestions(client, profileId);

  const counts = {
    youtube: ytData?.executedActions.length ?? 0,
    decisions: votesData?.myVotes.length ?? 0,
    pending: pendingData?.pendingSuggestions.length ?? 0,
  };

  const subheadText = SUBHEAD[active];

  return (
    <Box sx={{minHeight: '100vh', backgroundColor: '#F8FAFC'}}>
      {/* Single outer Container matching ProfileHome — no full-bleed
          white strips. Header, callout, tabs, subhead, and rows all
          sit inside one md-wide centered column on the slate-50 page
          background. */}
      <Container maxWidth="md" sx={{px: 3, py: 2}}>
        <Box sx={{mb: 2}}>
          <Typography
            sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
              color: '#64748B',
              fontSize: '10px',
              letterSpacing: '0.1em',
            }}
          >
            Profile
          </Typography>
          <Typography component="h1" sx={{fontWeight: 700, color: '#0F172A', fontSize: '20px', lineHeight: 1.2}}>
            Activity
          </Typography>
        </Box>

        {counts.pending > 0 && (
          <PendingCallout count={counts.pending} profileId={profileId} />
        )}

        <Box sx={{mt: 1}}>
          <TabsBar
            tabs={[
              {id: 'youtube', label: 'Feed changes', sub: 'Outcomes', count: counts.youtube},
              {id: 'decisions', label: 'My votes', sub: 'Audit trail', count: counts.decisions},
            ]}
            active={active === 'pending' ? 'youtube' : active}
            onChange={id => navigate(`/profile/${profileId}/activity/${id}`)}
          />
        </Box>

        {subheadText && <TabSubhead text={subheadText} />}

        {active !== 'decisions' && (
          <YouTubeTab
            client={client}
            profileId={profileId}
            selectedId={selectedYouTubeId}
            onSelect={setSelectedYouTubeId}
            persona={persona}
          />
        )}
        {active === 'decisions' && (
          <DecisionsTab
            client={client}
            profileId={profileId}
            selectedId={selectedDecisionId}
            onSelect={setSelectedDecisionId}
            persona={persona}
          />
        )}
      </Container>
    </Box>
  );
}
